import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Book, ArrowLeft, User, ScrollText, Headphones, Bookmark, Share2, Loader2 } from 'lucide-react';
import { generateShareCard } from '../utils/generateShareCard';
import {
  fetchSlokaById,
  fetchChapterByNumber,
  fetchCommentariesBySloka,
  getErrorMessage
} from '../api/client';
import SlokaArtImage from './SlokaArtImage';
import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';
import { SlokaRecitationAudio, MeaningRecitationAudio } from './VerseAudio';
import ErrorMessage from './ErrorMessage';
import WordBreakdown from './WordBreakdown';
import { useContinuousPlayer } from '../context/ContinuousPlayerContext';
import { useBookmarks } from '../hooks/useBookmarks';
import { useReadingProgress } from '../hooks/useReadingProgress';

const SlokaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { startPlayback } = useContinuousPlayer();
  const { isBookmarked, toggleBookmark } = useBookmarks();
  const { markRead } = useReadingProgress();
  const [sharing, setSharing] = useState(false);
  const [sloka, setSloka] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [commentaries, setCommentaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSlokaData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const slokaData = await fetchSlokaById(id);
      setSloka(slokaData);
      markRead(slokaData.chapterNumber, slokaData.slokaNumber);

      const [chapterData, commentaryData] = await Promise.all([
        fetchChapterByNumber(slokaData.language, slokaData.chapterNumber),
        fetchCommentariesBySloka(
          slokaData.language,
          slokaData.chapterNumber,
          slokaData.slokaNumber
        )
      ]);

      setChapter(chapterData);
      setCommentaries(commentaryData);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSlokaData();
  }, [loadSlokaData]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="skeleton h-10 w-40 rounded-xl" />
        <div className="card overflow-hidden p-0">
          <div className="p-6">
            <div className="flex gap-4 items-start mb-6">
              <div className="skeleton w-16 h-16 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-6 w-32" />
                <div className="skeleton h-4 w-48" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="skeleton h-4 w-24 mb-4" />
              <div className="skeleton h-20 w-full rounded-lg" />
              <div className="skeleton h-10 w-full rounded-lg mt-3" />
            </div>
            <div className="space-y-2 mt-6">
              <div className="skeleton h-4 w-20 mb-4" />
              <div className="skeleton h-4 w-full" />
              <div className="skeleton h-4 w-4/5" />
              <div className="skeleton h-4 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadSlokaData} />;
  }

  if (!sloka) {
    return (
      <div className="text-center py-12">
        <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Sloka not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center space-x-4">
        {chapter ? (
          <Link to={`/chapter/${chapter.chapterNumber}`} className="btn-secondary flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Chapter {chapter.chapterNumber}
          </Link>
        ) : (
          <Link to="/chapters" className="btn-secondary flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Chapters
          </Link>
        )}
      </div>

      <div className="card overflow-hidden p-0">
        <div className="sloka-detail-hero">
          <div className="sloka-detail-hero-body p-6">
        <div className="mb-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-gita-saffron rounded-full flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-xl">{sloka.chapterNumber}.{sloka.slokaNumber}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="chapter-title">Verse {sloka.slokaNumber}</h1>
              {chapter && (
                <p className="text-gray-600">
                  Chapter {chapter.chapterNumber}: {chapter.chapterName}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => toggleBookmark({
                id: sloka._id,
                chapterNumber: sloka.chapterNumber,
                slokaNumber: sloka.slokaNumber,
                sloka: sloka.sloka,
                meaning: sloka.meaning
              })}
              className={`shrink-0 p-2 rounded-full transition-colors ${
                isBookmarked(sloka._id)
                  ? 'text-gita-gold'
                  : 'text-gray-300 hover:text-gita-gold'
              }`}
              aria-label={isBookmarked(sloka._id) ? 'Remove bookmark' : 'Bookmark this verse'}
            >
              <Bookmark className={`w-6 h-6 ${isBookmarked(sloka._id) ? 'fill-current' : ''}`} />
            </button>
          </div>

          <div className="flex items-center space-x-3 mb-6">
            <span className="speaker-badge flex items-center">
              <User className="w-3 h-3 mr-1" />
              {sloka.speaker}
            </span>
            <span className="text-sm text-gray-500">Language: {sloka.language.toUpperCase()}</span>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="content-section-title">Sloka</h3>
            <div className="sloka-text bg-gray-50 p-6 rounded-lg text-center">
              {sloka.sloka}
            </div>
            <div className="mt-3">
              <SlokaRecitationAudio
                chapterNumber={sloka.chapterNumber}
                slokaNumber={sloka.slokaNumber}
              />
            </div>
          </div>

          <WordBreakdown
            chapterNumber={sloka.chapterNumber}
            slokaNumber={sloka.slokaNumber}
            language={sloka.language || DEFAULT_LANGUAGE}
          />

          <div>
            <h3 className="content-section-title">Meaning</h3>
            <div className="bg-orange-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">{sloka.meaning}</p>
            </div>
            <div className="mt-3">
              <MeaningRecitationAudio
                language={sloka.language || chapter?.language || DEFAULT_LANGUAGE}
                chapterNumber={sloka.chapterNumber}
                slokaNumber={sloka.slokaNumber}
              />
            </div>
          </div>

          {commentaries.length > 0 && (
            <div>
              <h3 className="content-section-title flex items-center">
                <ScrollText className="w-5 h-5 mr-2" />
                Commentary
              </h3>
              <div className="space-y-4">
                {commentaries.map((commentary) => (
                  <div key={commentary._id} className="bg-amber-50 border border-amber-100 p-6 rounded-lg">
                    {commentary.author && (
                      <p className="text-sm font-medium text-gita-maroon mb-3">
                        — {commentary.author}
                      </p>
                    )}
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {commentary.commentary}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {chapter && (
            <div>
              <h3 className="content-section-title">Chapter Context</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Chapter Name</p>
                  <p className="font-medium">{chapter.chapterName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Yoga Name</p>
                  <p className="font-medium">{chapter.yogaName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Meaning</p>
                  <p className="font-medium">{chapter.meaning}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Verses</p>
                  <p className="font-medium">{chapter.verseCount}</p>
                </div>
              </div>
            </div>
          )}

          {chapter && (
            <div className="flex flex-wrap gap-3 items-center pt-6 border-t">
              <Link to={`/chapter/${chapter.chapterNumber}`} className="btn-secondary flex items-center">
                View Full Chapter
              </Link>
              <button
                type="button"
                onClick={() => {
                  startPlayback({ startChapter: sloka.chapterNumber, endChapter: sloka.chapterNumber, variant: 'apple' });
                  navigate('/listen');
                }}
                className="btn-primary flex items-center"
              >
                <Headphones className="w-4 h-4 mr-2" />
                Listen to chapter
              </button>
              <button
                type="button"
                disabled={sharing}
                onClick={async () => {
                  setSharing(true);
                  try {
                    const blob = await generateShareCard(sloka);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `gita-${sloka.chapterNumber}-${sloka.slokaNumber}.png`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  } catch (e) {
                    console.error('Share card failed:', e);
                  } finally {
                    setSharing(false);
                  }
                }}
                className="btn-secondary flex items-center"
              >
                {sharing
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Share2 className="w-4 h-4 mr-2" />}
                Share verse
              </button>
            </div>
          )}
        </div>
          </div>

          <SlokaArtImage
            framed
            fallback="placeholder"
            frameVariant="light"
            chapterNumber={sloka.chapterNumber}
            slokaNumber={sloka.slokaNumber}
            alt={`Chapter ${sloka.chapterNumber}, Verse ${sloka.slokaNumber}`}
            frameClassName="sloka-detail-hero-art-frame"
            className="sloka-detail-hero-art"
          />
        </div>
      </div>
    </div>
  );
};

export default SlokaDetail;
